import { useQuery, gql, useMutation, QueryHookOptions } from '@apollo/client';
import {
  GQLRequest,
  GQLMutationRequest_ApprovePlatformProducerContractChangeRequestArgs,
  GQLQueryRequest_GetPlatformProducerContractChangeRequestByProducerArgs,
  GQLRequestConnection,
  GQLQueryRequest_GetPlatformProducerContractChangeRequestByPlatformArgs,
  GQLMutationRequest_CreatePlatformProducerContractChangeRequestArgs,
  GQLMutationRequest_CreatePlatformMerchantContractChangeRequestArgs,
  GQLMutationRequest_ApprovePlatformMerchantContractChangeRequestArgs,
  GQLQueryRequest_GetPlatformMerchantContractChangeRequestByMerchantArgs,
  GQLQueryRequest_GetPlatformMerchantContractChangeRequestByPlatformArgs
} from '@jewelutions/core-crm-server-api-client-domain';
import { REQUEST_FIELDS } from './fragments/request';
import { OperationsHelper } from './OperationHelper';

export class RequestOperation {
  requestArrayFromResponse(data: any): GQLRequest[] {
    return OperationsHelper.retrievePropertyFromGQLResponse(data, 'requests');
  }

  createPlatformProducerContractChangeRequest() {
    const mutation = gql`
      mutation Request_createPlatformProducerContractChangeRequest(
        $deliveryUnitId: ID!
        $deliveryUnitSaleCatalogId: ID!
      ) {
        Request_createPlatformProducerContractChangeRequest(
          deliveryUnitId: $deliveryUnitId
          deliveryUnitSaleCatalogId: $deliveryUnitSaleCatalogId
        )
      }
    `;
    return useMutation<String, GQLMutationRequest_CreatePlatformProducerContractChangeRequestArgs>(mutation);
  }

  approvePlatformProducerContractChangeRequest() {
    const mutation = gql`
      ${REQUEST_FIELDS}
      mutation Request_approvePlatformProducerContractChangeRequest(
        $accepted: Boolean!
        $note: String!
        $approverPartyId: ID!
        $requestId: ID!
      ) {
        Request_approvePlatformProducerContractChangeRequest(
          accepted: $accepted
          note: $note
          approverPartyId: $approverPartyId
          requestId: $requestId
        ) {
          ...RequestFields
        }
      }
    `;
    return useMutation<GQLRequest, GQLMutationRequest_ApprovePlatformProducerContractChangeRequestArgs>(
      mutation
    );
  }

  getPlatformProducerContractChangeRequestByProducer(
    args: GQLQueryRequest_GetPlatformProducerContractChangeRequestByProducerArgs,
    options: QueryHookOptions<
      GQLRequestConnection,
      GQLQueryRequest_GetPlatformProducerContractChangeRequestByProducerArgs
    > = {}
  ) {
    const query = gql`
      ${REQUEST_FIELDS}
      query Request_getPlatformProducerContractChangeRequestByProducer(
        $producerPartyId: ID!
        $deliveryUnitId: ID!
        $completed: Boolean!
      ) {
        Request_getPlatformProducerContractChangeRequestByProducer(
          producerPartyId: $producerPartyId
          deliveryUnitId: $deliveryUnitId
          completed: $completed
        ) {
          cursor
          hasMore
          requests {
            ...RequestFields
          }
        }
      }
    `;
    return useQuery<
      GQLRequestConnection,
      GQLQueryRequest_GetPlatformProducerContractChangeRequestByProducerArgs
    >(query, {
      ...options,
      variables: args
    });
  }

  getPlatformProducerContractChangeRequestByPlatform(
    args: GQLQueryRequest_GetPlatformProducerContractChangeRequestByPlatformArgs,
    options: QueryHookOptions<
      GQLRequestConnection,
      GQLQueryRequest_GetPlatformProducerContractChangeRequestByPlatformArgs
    > = {}
  ) {
    const query = gql`
      ${REQUEST_FIELDS}
      query Request_getPlatformProducerContractChangeRequestByPlatform($completed: Boolean!) {
        Request_getPlatformProducerContractChangeRequestByPlatform(completed: $completed) {
          cursor
          hasMore
          requests {
            ...RequestFields
          }
        }
      }
    `;
    return useQuery<
      GQLRequestConnection,
      GQLQueryRequest_GetPlatformProducerContractChangeRequestByPlatformArgs
    >(query, { ...options, variables: args });
  }

  createPlatformMerchantContractChangeRequest() {
    const mutation = gql`
      mutation Request_createPlatformMerchantContractChangeRequest(
        $merchantPartyId: ID!
        $platformSaleCatalogId: ID!
      ) {
        Request_createPlatformMerchantContractChangeRequest(
          merchantPartyId: $merchantPartyId
          platformSaleCatalogId: $platformSaleCatalogId
        )
      }
    `;
    return useMutation<String, GQLMutationRequest_CreatePlatformMerchantContractChangeRequestArgs>(mutation);
  }

  approvePlatformMerchantContractChangeRequest() {
    const mutation = gql`
      ${REQUEST_FIELDS}
      mutation Request_approvePlatformMerchantContractChangeRequest(
        $accepted: Boolean!
        $note: String!
        $approverPartyId: ID!
        $requestId: ID!
      ) {
        Request_approvePlatformMerchantContractChangeRequest(
          accepted: $accepted
          note: $note
          approverPartyId: $approverPartyId
          requestId: $requestId
        ) {
          ...RequestFields
        }
      }
    `;
    return useMutation<GQLRequest, GQLMutationRequest_ApprovePlatformMerchantContractChangeRequestArgs>(
      mutation
    );
  }

  getPlatformMerchantContractChangeRequestByMerchant(
    args: GQLQueryRequest_GetPlatformMerchantContractChangeRequestByMerchantArgs,
    options: QueryHookOptions<
      GQLRequestConnection,
      GQLQueryRequest_GetPlatformMerchantContractChangeRequestByMerchantArgs
    > = {}
  ) {
    const query = gql`
      ${REQUEST_FIELDS}
      query Request_getPlatformMerchantContractChangeRequestByMerchant(
        $merchantPartyId: ID!
        $completed: Boolean!
      ) {
        Request_getPlatformMerchantContractChangeRequestByMerchant(
          merchantPartyId: $merchantPartyId
          completed: $completed
        ) {
          cursor
          hasMore
          requests {
            ...RequestFields
          }
        }
      }
    `;
    return useQuery<
      GQLRequestConnection,
      GQLQueryRequest_GetPlatformMerchantContractChangeRequestByMerchantArgs
    >(query, {
      ...options,
      variables: args
    });
  }

  getPlatformMerchantContractChangeRequestByPlatform(
    args: GQLQueryRequest_GetPlatformMerchantContractChangeRequestByPlatformArgs,
    options: QueryHookOptions<
      GQLRequestConnection,
      GQLQueryRequest_GetPlatformMerchantContractChangeRequestByPlatformArgs
    > = {}
  ) {
    const query = gql`
      ${REQUEST_FIELDS}
      query Request_getPlatformMerchantContractChangeRequestByPlatform($completed: Boolean!) {
        Request_getPlatformMerchantContractChangeRequestByPlatform(completed: $completed) {
          cursor
          hasMore
          requests {
            ...RequestFields
          }
        }
      }
    `;
    return useQuery<
      GQLRequestConnection,
      GQLQueryRequest_GetPlatformMerchantContractChangeRequestByPlatformArgs
    >(query, { ...options, variables: args });
  }
}
